/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-require-imports */
import { TSNi18n } from "../../common/_i18n";

const initLocale = (editionLocale) => {
    const i18nApp = require("gettext.js").default;
    const i18n = i18nApp();

    const jsonData = require(`../../../locale/messages-${editionLocale}.json`);
    i18n.loadJSON(jsonData);
    i18n.setLocale(editionLocale);

    const dayjs = require("dayjs");
    try {
        require(`dayjs/locale/${editionLocale}.js`);
        dayjs.locale(editionLocale);
    } catch {
        const shortLocale = editionLocale.split("-")[0];
        console.log(
            `dayjs: locale ${editionLocale} not found, falling back to locale ${shortLocale}`,
        );
        require(`dayjs/locale/${shortLocale}.js`);
        dayjs.locale(shortLocale);
    }

    return [i18n, dayjs];
};

export const getShortDateFormatForLocale = (localeRaw) => {
    const locale = (localeRaw || "en-us").toLowerCase();
    switch (locale) {
        case "en-us":
        case "en-ca":
            return "MM-DD-YYYY";
        case "ja-jp":
        case "ko-kr":
            return "YYYY-MM-DD";
        default:
            return "DD-MM-YYYY";
    }
};

const formatPublishedOn = (
    publishedOn,
    tsni18n,
    i18n,
    dayjs,
    lessThanOneDay = false,
    longDate = false,
    showUpdated = false,
    dateFormatOverride = null,
    isLongTimeFormat = false,
) => {
    const editionLocale = i18n.getLocale();
    const publishedDate = dayjs(publishedOn);
    const currentDate = dayjs();
    const updatedPrefix = showUpdated ? "Updated " : "";
    const minuteSuffix = showUpdated ? "" : "ute";

    const diffInHours = currentDate.diff(publishedDate, "hour");
    let diffInMinutes = currentDate.diff(publishedDate, "minute");

    let dateFormat = "";
    let returnDate = "";
    let returnTime = "";

    try {
        // Date logic
        if (lessThanOneDay && diffInMinutes < 60) {
            // We still display "1 minute ago" even when the article was just published
            diffInMinutes = Math.max(diffInMinutes, 1);

            if (longDate) {
                const localisedString = i18n.ngettext(
                    `${updatedPrefix}%(count)s min${minuteSuffix} ago`,
                    `${updatedPrefix}%(count)s min${minuteSuffix}s ago`,
                    diffInMinutes,
                );
                returnDate = localisedString.replace(
                    "%(count)s",
                    diffInMinutes,
                );
            } else {
                // If datetime is in NESN skin, change format of minutes
                const localisedString = i18n.gettext("%(count)sm");
                if (!isLongTimeFormat) {
                    returnDate = localisedString.replace(
                        "%(count)s",
                        diffInMinutes,
                    );
                } else {
                    let unit = "Minutes";
                    if (diffInMinutes === 1) {
                        unit = "Minute";
                    }
                    returnDate = localisedString.replace(
                        "%(count)sm",
                        `${diffInMinutes} ${unit} Ago`,
                    );
                }
            }
        } else if (lessThanOneDay && diffInHours < 24) {
            if (longDate) {
                const localisedString = i18n.ngettext(
                    `${updatedPrefix}%(count)s hour ago`,
                    `${updatedPrefix}%(count)s hours ago`,
                    diffInHours,
                );
                returnDate = localisedString.replace("%(count)s", diffInHours);
            } else {
                // If datetime is in NESN skin, change format of hours
                const localisedString = i18n.gettext("%(count)shr");
                if (!isLongTimeFormat) {
                    returnDate = localisedString.replace(
                        "%(count)s",
                        diffInHours,
                    );
                } else {
                    let unit = "Hours";
                    if (diffInHours === 1) {
                        unit = "Hour";
                    }
                    returnDate = localisedString.replace(
                        "%(count)shr",
                        `${diffInHours} ${unit} Ago`,
                    );
                }
            }
        } else {
            if (longDate) {
                if (dateFormatOverride) {
                    returnDate = publishedDate.format(dateFormatOverride);
                } else {
                    const dateObj = new Date(publishedOn);
                    const dateFormatter = new Intl.DateTimeFormat(
                        editionLocale,
                        {
                            dateStyle: "long",
                            calendar: "gregory",
                        },
                    );
                    returnDate = dateFormatter.format(dateObj);
                }
            } else {
                dateFormat = getShortDateFormatForLocale(editionLocale);
                returnDate = publishedDate.format(dateFormat);
            }
        }

        // Time logic
        if (longDate) {
            const dateObj = new Date(publishedOn);
            const currentTimeZone =
                Intl.DateTimeFormat().resolvedOptions().timeZone;
            const timeZoneAbbreviation = tsni18n.getTimeZoneAbbreviation(
                dateObj,
                currentTimeZone,
            );

            const timeFormatter = new Intl.DateTimeFormat("en-us", {
                hour: "numeric",
                minute: "numeric",
                hourCycle: "h12",
            });
            const timeString = timeFormatter.format(dateObj);

            returnTime = `${timeString.toLowerCase()} ${timeZoneAbbreviation}`;
        }
    } catch (error) {
        console.error("Parsing error:", error.message);
        // Fall back to blank values in case of error
        returnDate = "";
        returnTime = "";
    }

    return [returnDate, returnTime];
};

const longDateDifference = (dateTimeSelector, showUpdated) => {
    const published = document.querySelector(dateTimeSelector);

    if (published) {
        const tsnEdition = published.getAttribute("data-tsnedition");
        const publishedOnTimestamp = published.getAttribute("datetime");
        const dateFormatOverride = published.getAttribute(
            "date-format-override",
        );
        const tsni18n = new TSNi18n();
        const editionLocale = tsni18n.getLocaleFromEdition(tsnEdition);
        const [i18n, dayjs] = initLocale(editionLocale);
        const [formattedDate, formattedTime] = formatPublishedOn(
            publishedOnTimestamp,
            tsni18n,
            i18n,
            dayjs,
            true,
            true,
            showUpdated,
            dateFormatOverride,
        );
        let publishedDate = document.querySelector(
            `${dateTimeSelector} > .published-date`,
        );
        if (publishedDate) {
            publishedDate.innerHTML = formattedDate;
        }
        let publishedTime = document.querySelector(
            `${dateTimeSelector} > .published-time`,
        );
        if (publishedTime) {
            publishedTime.innerHTML = formattedTime;
        }
    }
};

export const publishedLongDateDifference = () => {
    longDateDifference(".published-datetime-long", false);
};

export const liveBlogLongDateDifference = () => {
    longDateDifference(".published-datetime-long-live-blog", true);
};

export const publishedShortDateDifference = () => {
    const published = document.querySelector(".published-datetime-short");

    if (published) {
        const tsnEdition = published.getAttribute("data-tsnedition");
        const publishedOnTimestamp = published.getAttribute("datetime");
        const tsni18n = new TSNi18n();
        const editionLocale = tsni18n.getLocaleFromEdition(tsnEdition);
        const [i18n, dayjs] = initLocale(editionLocale);
        const [formattedDate, _] = formatPublishedOn(
            publishedOnTimestamp,
            tsni18n,
            i18n,
            dayjs,
        );
        published.innerHTML = formattedDate;
    }
};

export const updateListingPublishedFormats = () => {
    const tsni18n = new TSNi18n();
    const editionLocale = tsni18n.isLocaleExists(
        document.querySelector("html").lang,
    );
    const [i18n, dayjs] = initLocale(editionLocale);
    const isLongTime = checkIfLongTimeFormat();

    const elements = document.querySelectorAll("time[datetime]");
    elements.forEach((element) => {
        const publishedOnTimestamp = element.getAttribute("datetime");
        const [formattedDate, _] = formatPublishedOn(
            publishedOnTimestamp,
            tsni18n,
            i18n,
            dayjs,
            true,
            false,
            false,
            null,
            isLongTime,
        );
        element.textContent = formattedDate;
    });
};

// Check if stylesheet is NESN (Time layout with long time format)
const checkIfLongTimeFormat = () => {
    const links = document.querySelectorAll('link[rel="stylesheet"]');

    for (const link of links) {
        const href = link.getAttribute("href");
        if (!href) continue;

        if (href.endsWith("skin-nesn.css")) {
            return true;
        }
    }

    return false;
};
